import { z } from "zod";

/**
 * Schema for every environment variable the frontend requires at runtime.
 * Add new required vars here — this file (and its startup validation) is
 * the single source of truth; there is no separate `.env.example` to keep
 * in sync.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z
    .string({ error: "VITE_API_BASE_URL is required (e.g. http://localhost:8000/v1)" })
    .min(1, "VITE_API_BASE_URL must not be empty")
    .url("VITE_API_BASE_URL must be a valid absolute URL"),
  VITE_GOOGLE_CLIENT_ID: z
    .string({ error: "VITE_GOOGLE_CLIENT_ID is required for Google sign-in" })
    .min(1, "VITE_GOOGLE_CLIENT_ID must not be empty"),
});

export type Env = z.infer<typeof envSchema>;

function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => `  - ${issue.path.join(".") || "(root)"}: ${issue.message}`)
    .join("\n");
}

/**
 * Validates `import.meta.env` against `envSchema` and throws a single,
 * actionable error listing every missing/invalid variable. Called once at
 * module load so a misconfigured deployment fails fast at startup instead
 * of surfacing as a confusing runtime error deep in a feature.
 */
function loadEnv(): Env {
  const result = envSchema.safeParse(import.meta.env);
  if (!result.success) {
    throw new Error(
      `Invalid environment configuration. Fix the following and restart:\n${formatIssues(
        result.error,
      )}\n\nSet these in a local .env file (see README.md for the full table) ` +
        "or in your deployment environment.",
    );
  }
  return result.data;
}

export const env: Env = loadEnv();
